/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, computed, observable } from 'mobx';
import {
    AsyncActionInfo,
    AsyncActionStatusUpdatedEvent,
    PermanentNotification,
    ServerEventNotification,
    ServerEventType,
} from '@veeam-vspc/models/rest';
import { TimeUnitsInMs } from '@veeam-vspc/core';
import { renderShortDateAndTimeForColumn } from '@veeam-vspc/components';

import type { BaseRequestResponse, BaseSuccessRequestResponse, TransportService } from '@veeam-vspc/core';
import type {
    // AsyncActionAddedEvent,
    // AsyncActionRemovedEvent,
    AsyncActionStatus,
    PermanentNotificationAddedEvent,
    PermanentNotificationsDismissedEvent,
    PermanentNotificationUpdatedEvent,
    UpdateServerEventsListenSettings,
} from '@veeam-vspc/models/rest';

import { BellMessageTypes, ConnectionStatuses } from '../enums';
import { WebSocketConnections } from 'core/enums';
import { getStatusIconSrc, markdownToHtml } from 'core/utils';
import { getActionNameHelper } from '../utils';

import type { LangsServiceBase } from 'core/services/langs/interfaces';
import type { BellMessage } from '../interfaces';
import type { PortalFormats } from 'core/entries/portal-data/interfaces';

export class AppBellStore {

    @observable messages: BellMessage[] = [];

    connectionStatus: ConnectionStatuses = ConnectionStatuses.None;

    protected dismissEventPromise: Promise<BaseRequestResponse<any>> | null = null;
    protected intervalId: number | null = null;

    constructor(protected transportService: TransportService, protected formats: PortalFormats, protected lang: LangsServiceBase) {
        this.setRefreshInterval();
    }

    connect() {
        return this.getInitialStateWithRespectDismiss()
            .then(() => this.connectToEvents());
    }

    @computed
    get unread(): boolean {
        return this.messages?.length > 0;
    }

    setRefreshInterval() {
        this.intervalId = window.setInterval(() => {
            if (this.transportService.config.getIsAutoUpdateBlocked()) {
                clearInterval(this.intervalId);
                return;
            }

            return this.getInitialStateWithRespectDismiss();
        }, 5 * TimeUnitsInMs.Minute);
    }

    getInitialStateWithRespectDismiss() {
        if (this.dismissEventPromise) {
            return this.dismissEventPromise
                .then(() => {
                    this.dismissEventPromise = null;

                    return this.getInitialState();
                })
                .catch(() => {
                    this.dismissEventPromise = null;

                    return this.getInitialState();
                });
        } else {
            return this.getInitialState();
        }
    }

    @action.bound
    getInitialState(): Promise<void> {
        return Promise.all([
            this.transportService.get<{ rcopAutoUpdate: true; }, PermanentNotification[]>('/PermanentNotifications', { rcopAutoUpdate: true }),
            this.transportService.get<{ rcopAutoUpdate: true; }, AsyncActionInfo[]>('/AsyncActions', { rcopAutoUpdate: true }),
        ])
            .then(([notificationsResp = {}, asyncActionsResp = {}]) => {
                // const { data: asyncActionItems = [] } = asyncActionsResp as BaseSuccessRequestResponse<AsyncActionInfo[]>;
                const { data: notificationItems = [] } = notificationsResp as BaseSuccessRequestResponse<PermanentNotification[]>;
                // const modifiedAsyncActionMessages = asyncActionItems
                //     .map(item => this.mapAsyncAction(item))
                //     .filter(item => item);
                const modifiedPermanentNotificationMessages = notificationItems.map(item => this.mapPermanentNotification(item));

                // this.messages = modifiedAsyncActionMessages.concat(modifiedPermanentNotificationMessages);
                this.messages = modifiedPermanentNotificationMessages;
            })
            .catch((resp) => {
                resp && console.error(resp);
            });
    }

    @action.bound
    connectToEvents(): Promise<void> {
        if (this.connectionStatus === ConnectionStatuses.Success) {
            return;
        }

        this.connectionStatus = ConnectionStatuses.Connecting;

        return this.transportService.connectToWs(WebSocketConnections.NotificationBell, '/ServerEvents/Subscribe', {
            skipBaseUrl: true,
            onWebSocketClose: () => {
                this.connectionStatus = ConnectionStatuses.None;
                setTimeout(() => this.connect(), 1 * TimeUnitsInMs.Second);
            },
            onWebSocketError: () => {
                this.connectionStatus = ConnectionStatuses.Failed;
            },
        })
            .then(() => {
                this.connectionStatus = ConnectionStatuses.Success;

                const eventsToListen = {
                    eventsToListen: [
                        ServerEventType.PermanentNotificationsDismissedEvent,
                        ServerEventType.PermanentNotificationUpdatedEvent,
                        ServerEventType.PermanentNotificationAddedEvent,
                        // ServerEventType.AsyncActionAddedEvent,
                        // ServerEventType.AsyncActionStatusUpdatedEvent,
                        // ServerEventType.AsyncActionRemovedEvent,
                    ],
                } as UpdateServerEventsListenSettings;

                return this.transportService.sendToWs(WebSocketConnections.NotificationBell, JSON.stringify(eventsToListen));
            })
            .catch(() => {
                // transport service block any connections (in case of auto logout)
            });
    }

    subscribeToEvents() {
        return this.transportService.subscribeToWs(WebSocketConnections.NotificationBell, (message: string) => {
            try {
                this.onNotificationBellData(JSON.parse(message));
            } catch (e) {
                console.error('Failed to parse web socket message', e);
            }
        });
    }

    @action.bound
    onNotificationBellData(serverEvent: ServerEventNotification) {
        const { type, content } = serverEvent;

        switch (type) {
            case (ServerEventType.PermanentNotificationAddedEvent):
                return this.addNewPermanentEvent((content as PermanentNotificationAddedEvent).notificationContent);
            case (ServerEventType.PermanentNotificationsDismissedEvent):
                return this.removeNotification((content as PermanentNotificationsDismissedEvent).notificationUid);
            case (ServerEventType.PermanentNotificationUpdatedEvent):
                return this.updatePermanentNotificationEvent((content as PermanentNotificationUpdatedEvent).notificationContent);
            // case (ServerEventType.AsyncActionAddedEvent):
            //     return this.addNewAsyncAction((content as AsyncActionAddedEvent).asyncActionInfo);
            // case (ServerEventType.AsyncActionRemovedEvent):
            //     return this.removeNotification((content as AsyncActionRemovedEvent).asyncActionId);
            // case (ServerEventType.AsyncActionStatusUpdatedEvent):
            //     return this.updateAsyncActionStatus(content);
            default: return;
        }
    }

    dismissEvent(type: BellMessageTypes, eventId: string): Promise<BaseRequestResponse<{}>> {
        this.messages = this.messages.filter(({ id }) => id !== eventId);

        switch (type) {
            case (BellMessageTypes.AsyncAction): return (this.dismissEventPromise = this.transportService.delete(`/AsyncActions/${eventId}`));
            case (BellMessageTypes.Permanent): return (this.dismissEventPromise = this.transportService.delete(`/PermanentNotifications/${eventId}`));
        }
    }

    protected mapPermanentNotification(item: PermanentNotification): BellMessage {
        return {
            id: item.instanceUid,
            title: markdownToHtml(item.title),
            titleIcon: getStatusIconSrc(item.severity),
            text: markdownToHtml(item.content),
            creationTime: renderShortDateAndTimeForColumn(item.updateTimeStamp, this.formats.netShortDate, this.formats.netShortTime),
            type: BellMessageTypes.Permanent,
            permanentNotificationPayload: item,
            onCloseClick: () => this.dismissEvent(BellMessageTypes.Permanent, item.instanceUid),
        };
    }

    protected mapAsyncAction(item: AsyncActionInfo): BellMessage {
        const { id, actionName, status } = item;
        const title: string | null = getActionNameHelper(actionName, status, this.lang);

        if (!title) {
            return null;
        }

        return {
            id,
            title,
            titleIcon: getStatusIconSrc(status),
            type: BellMessageTypes.AsyncAction,
            asyncActionPayload: item,
            onCloseClick: () => this.dismissEvent(BellMessageTypes.AsyncAction, item.id),
        };
    }

    protected updateStatusInAsyncAction(item: BellMessage, newStatus: AsyncActionStatus): BellMessage {
        const title: string | null = getActionNameHelper(item.asyncActionPayload?.actionName, newStatus, this.lang);

        return {
            ...item,
            title: title || item.title,
            titleIcon: getStatusIconSrc(newStatus),
            asyncActionPayload: {
                ...item.asyncActionPayload,
                status: newStatus,
            },
        };
    }

    @action.bound
    protected addNewPermanentEvent(content: PermanentNotification): void {
        const { instanceUid } = content;

        if (!this.messages.find(({ id }) => id === instanceUid)) {
            this.messages.unshift(this.mapPermanentNotification(content));
        }
    }

    @action.bound
    protected addNewAsyncAction(content: AsyncActionInfo): void {
        const { id: asyncActionId } = content;

        if (!this.messages.find(({ id }) => id === asyncActionId)) {
            const asyncActionItem: BellMessage | null = this.mapAsyncAction(content);

            // show only selected async actions
            asyncActionItem && this.messages.unshift(asyncActionItem);
        }
    }

    @action.bound
    protected updatePermanentNotificationEvent(content: PermanentNotification): void {
        const { instanceUid } = content;
        this.messages = this.messages.map(item => item.id === instanceUid ? this.mapPermanentNotification(content) : item);
    }

    @action.bound
    protected removeNotification(eventId: string): void {
        this.messages = this.messages.filter(({ id }) => id !== eventId);
    }

    @action.bound
    protected updateAsyncActionStatus({ id, status }: AsyncActionStatusUpdatedEvent): void {
        this.messages = this.messages.map(item => item.id === id ? this.updateStatusInAsyncAction(item, status) : item);
    }

}
